import React, { useEffect, useRef, useState } from 'react';
import { OverlayPanel } from 'primereact/overlaypanel';
import { TreeSelectItemInfoContent } from './TreeSelectItemInfoContent';
import AutoBillingBadge from './AutoBillingBadge';

export const TREE_SELECT_LABEL_TOOLTIP_CLASS = 'tree-select-label-tooltip';
export const TREE_SELECT_NODE_INFO_CLASS = 'tree-select-node-info';

export const truncateLabel = (text, max = 30) =>
    text?.length > max ? `${text.substring(0, max)}...` : text;

export const getNodeFullLabel = (node) =>
    node?.fullLabel ?? node?.data?.name ?? node?.label ?? '';

export const isPlanTreeNode = (node) => Boolean(node?.data?.policy);

export const isNodeLabelTruncated = (node) => {
    const fullLabel = getNodeFullLabel(node);
    if (!fullLabel || !node?.label) return false;
    return fullLabel !== node.label;
};

export const shouldShowTreeSelectTooltip = (node, maxLength = 30) => {
    const fullLabel = getNodeFullLabel(node);
    return Boolean(fullLabel && (fullLabel.length > maxLength || isNodeLabelTruncated(node)));
};

export const shouldShowTreeSelectItemInfo = (node) =>
    Boolean(getNodeFullLabel(node));

export const getProductInfoPayload = (product) => {
    if (!product?.name) return null;

    return {
        type: 'product',
        name: product.name,
        description: product.description ?? '',
        exclusive: product.exclusive,
    };
};

export const getTreeSelectItemInfoPayload = (node) => {
    const data = node?.data;
    const name = getNodeFullLabel(node);

    if (!name) return null;

    if (isPlanTreeNode(node) && data) {
        return {
            type: 'plan',
            name,
            description: data.description ?? '',
            policy: data.policy,
            billingPeriod: data.billingPeriod,
            cost: data.cost,
            deductions: data.deductions,
            setup: data.setup,
            graceDays: data.graceDays,
            reminderDays: data.reminderDays,
            targetEqualParts: data.targetEqualParts,
            recoverMissed: data.recoverMissed,
            collectionMode: data.collectionMode,
        };
    }

    return getProductInfoPayload({ ...data, name });
};

const stopTreeSelectEvent = (event) => {
    event.stopPropagation();
};

let activeOverlayRef = null;

export const withTruncatedTreeLabels = (nodes, maxLength = 30) => {
    if (!nodes?.length) return [];

    return nodes.map((node) => {
        const fullLabel = getNodeFullLabel(node);
        const nextNode = {
            ...node,
            fullLabel,
            label: truncateLabel(fullLabel, maxLength),
        };

        if (node.children?.length) {
            nextNode.children = withTruncatedTreeLabels(node.children, maxLength);
        }

        return nextNode;
    });
};

export const TreeSelectLabelTooltip = () => {
    const overlayRef = useRef(null);
    const hideTimerRef = useRef(null);
    const activeIconRef = useRef(null);
    const itemInfoRef = useRef(null);
    const overlayListenersRef = useRef(null);
    const [, setRenderVersion] = useState(0);

    if (!activeOverlayRef) {
        activeOverlayRef = overlayRef;
    }
    const isOwner = activeOverlayRef === overlayRef;

    useEffect(() => {
        return () => {
            if (activeOverlayRef === overlayRef) {
                activeOverlayRef = null;
            }
        };
    }, []);

    const clearHideTimer = () => {
        if (hideTimerRef.current) {
            clearTimeout(hideTimerRef.current);
            hideTimerRef.current = null;
        }
    };

    const detachOverlayListeners = () => {
        const listeners = overlayListenersRef.current;
        const overlayEl = overlayRef.current?.getElement?.();
        if (!listeners || !overlayEl) return;

        overlayEl.removeEventListener('mouseenter', listeners.onEnter);
        overlayEl.removeEventListener('mouseleave', listeners.onLeave);
        overlayListenersRef.current = null;
    };

    const hideOverlay = () => {
        clearHideTimer();
        detachOverlayListeners();
        overlayRef.current?.hide();
        itemInfoRef.current = null;
        setRenderVersion((version) => version + 1);
        activeIconRef.current = null;
    };

    const scheduleHide = () => {
        clearHideTimer();
        hideTimerRef.current = setTimeout(hideOverlay, 120);
    };

    const showForIcon = (event, icon) => {
        const encoded = icon.getAttribute('data-item-info');
        if (!encoded) return;

        try {
            const parsed = JSON.parse(decodeURIComponent(encoded));
            clearHideTimer();
            activeIconRef.current = icon;
            itemInfoRef.current = parsed;
            setRenderVersion((version) => version + 1);
            overlayRef.current?.show(event, icon);
        } catch {
            // ignore invalid payload
        }
    };

    useEffect(() => {
        if (!isOwner) return;

        const handleMouseOver = (event) => {
            const icon = event.target.closest(`.${TREE_SELECT_NODE_INFO_CLASS}`);
            if (!icon) return;
            if (icon === activeIconRef.current) return;
            showForIcon(event, icon);
        };

        const handleMouseOut = (event) => {
            const related = event.relatedTarget;
            const overlayEl = overlayRef.current?.getElement?.();
            const icon = event.target.closest?.(`.${TREE_SELECT_NODE_INFO_CLASS}`);

            if (related && overlayEl?.contains(related)) {
                clearHideTimer();
                return;
            }

            if (icon && related && icon.contains(related)) {
                return;
            }

            if (icon || (activeIconRef.current && event.target === activeIconRef.current)) {
                scheduleHide();
            }
        };

        document.addEventListener('mouseover', handleMouseOver);
        document.addEventListener('mouseout', handleMouseOut);

        return () => {
            document.removeEventListener('mouseover', handleMouseOver);
            document.removeEventListener('mouseout', handleMouseOut);
            clearHideTimer();
            detachOverlayListeners();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOwner]);

    const handleOverlayShow = () => {
        const overlayEl = overlayRef.current?.getElement?.();
        if (!overlayEl) return;

        const onEnter = () => clearHideTimer();
        const onLeave = (event) => {
            const related = event.relatedTarget;
            if (related && activeIconRef.current?.contains(related)) {
                return;
            }
            scheduleHide();
        };

        overlayEl.addEventListener('mouseenter', onEnter);
        overlayEl.addEventListener('mouseleave', onLeave);
        overlayListenersRef.current = { onEnter, onLeave };
    };

    const handleOverlayHide = () => {
        detachOverlayListeners();
        itemInfoRef.current = null;
        setRenderVersion((version) => version + 1);
        activeIconRef.current = null;
    };

    if (!isOwner) {
        return null;
    }

    return (
        <OverlayPanel
            ref={overlayRef}
            className="tree-select-item-overlay planInfoTooltip"
            dismissable={false}
            appendTo={() => document.body}
            onShow={handleOverlayShow}
            onHide={handleOverlayHide}
        >
            {itemInfoRef.current ? <TreeSelectItemInfoContent itemInfo={itemInfoRef.current} /> : null}
        </OverlayPanel>
    );
};

export const renderItemInfoIcon = (payload) => {
    if (!payload) return null;

    return (
        <i
            className={`pi pi-info-circle ${TREE_SELECT_NODE_INFO_CLASS}`}
            data-item-info={encodeURIComponent(JSON.stringify(payload))}
            aria-label={payload.name}
            onMouseDown={stopTreeSelectEvent}
            onClick={stopTreeSelectEvent}
        />
    );
};

const renderTreeSelectItemInfo = (node) => {
    const payload = getTreeSelectItemInfoPayload(node);
    if (!shouldShowTreeSelectItemInfo(node) || !payload) return null;

    return renderItemInfoIcon(payload);
};

export const renderProductDropdownItem = (product) => {
    const payload = getProductInfoPayload(product);

    return (
        <span className="tree-select-node-label">
            {renderItemInfoIcon(payload)}
            <span className="tree-select-node-text">{product.name}</span>
        </span>
    );
};

export const renderTreeSelectLabel = (node, maxLength = 30, showItemInfo = true) => {
    return (
        <span className="tree-select-node-label">
            {showItemInfo ? renderTreeSelectItemInfo(node) : null}
            <span className="tree-select-node-text">{node.label}</span>
            <AutoBillingBadge collectionMode={node?.data?.collectionMode} />
        </span>
    );
};

export const createTreeSelectNodeTemplate = (showItemInfo = true) => (node) =>
    renderTreeSelectLabel(node, 30, showItemInfo);

export const createTreeSelectValueTemplate = (showItemInfo = true) => (selectedNodes, props) => {
    if (!selectedNodes?.length) {
        return props?.placeholder
            ? <span className="p-treeselect-placeholder">{props.placeholder}</span>
            : null;
    }

    const node = selectedNodes[0];
    const selectedLabel = getNodeFullLabel(node) || node.label;

    return (
        <span className="tree-select-node-label tree-select-value-label">
            {showItemInfo ? renderTreeSelectItemInfo(node) : null}
            <span className="tree-select-node-text" title={selectedLabel}>{truncateLabel(selectedLabel, 18)}</span>
            <AutoBillingBadge collectionMode={node?.data?.collectionMode} />
        </span>
    );
};

export const treeSelectNodeTemplate = (node) => renderTreeSelectLabel(node);

export const treeSelectValueTemplate = createTreeSelectValueTemplate(true);
